import {
  DateTimeSearchType,
  NumericSearchTypeEnum,
  SortByType,
} from 'constants-activate';
import { FilterState } from '../types';
import { FeatureInstance } from 'lane-shared/types/FeatureInstance';

type InteractionSearchParams = {
  sortBy: SortByType;
  _created?: DateTimeSearchType;
  startDate?: DateTimeSearchType;
};

function buildDateRangeSearch(
  startDate?: Date,
  endDate?: Date
): DateTimeSearchType | undefined {
  if (startDate && endDate) {
    return {
      type: NumericSearchTypeEnum.Between,
      value: startDate,
      valueB: endDate,
    };
  }

  if (startDate && !endDate) {
    return {
      type: NumericSearchTypeEnum.GreaterThanOrEqual,
      value: startDate,
    };
  }

  if (!startDate && endDate) {
    return {
      type: NumericSearchTypeEnum.LessThanOrEqual,
      value: endDate,
    };
  }

  return undefined;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);

  d.setHours(0, 0, 0, 0);

  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);

  d.setHours(23, 59, 59, 999);

  return d;
}

/**
 * Build search parameters from filters for GraphQL query
 */
export function buildSearchParams(
  filters: FilterState,
  isReservableContent: boolean
): InteractionSearchParams {
  const params: InteractionSearchParams = {
    sortBy: {
      key: filters.sortBy,
      dir: filters.sortDirection,
    },
  };

  // Created date of interaction (bookingDateRange maps to _created)
  // If user selected only a single "Created date", interpret as that day's range
  if (!filters.bookingDateRange.startDate && filters.bookingDateRange.endDate) {
    const day = filters.bookingDateRange.endDate;

    params._created = {
      type: NumericSearchTypeEnum.Between,
      value: startOfDay(day),
      valueB: endOfDay(day),
    };
  } else {
    // Fallback: generic date range
    params._created = buildDateRangeSearch(
      filters.bookingDateRange.startDate,
      filters.bookingDateRange.endDate
    );
  }

  // Start date of Reservation (maps to startDate)
  if (isReservableContent) {
    const { startDate, endDate } = filters.reservationDateRange || {};

    if (startDate && !endDate) {
      // Exact calendar day for reservation start
      params.startDate = {
        type: NumericSearchTypeEnum.Between,
        value: startOfDay(startDate),
        valueB: endOfDay(startDate),
      };
    } else {
      // Fallback: generic date range
      params.startDate = buildDateRangeSearch(startDate, endDate);
    }
  }

  return params;
}

export function isContentReservable(features: FeatureInstance[]): boolean {
  return (
    features?.some(
      (feature: { type?: string | null }) => feature.type === 'Reservable'
    ) ?? false
  );
}
